import { Autocomplete, DatePicker, Input } from '@fullscreendigital/carrefour-dex-fe'
import { Grid } from '@mui/material'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import s from '../ReportModal.modules.scss'
import dayjs from 'dayjs'
import {
  Carrier,
  CarrierOptionType,
  CollectingCompanyTableItem,
  CompanyOptionType,
  DEEEReportFormInterface,
  Delegate,
  DelegateOptionType
} from 'components/waste-management/interfaces'
import { useSelectSearch } from 'hooks/useSelectSearch'
import { getNonNegativeNumberInputHandlers } from '../helpers'

const NonDangerousDEEEReportForm = (props: DEEEReportFormInterface) => {
  const {
    formik,
    currentCompany,
    operationTypesOptions,
    currentLocationInfo,
    isExternalArticlesReport
  } = props

  const carrierSelectSearch = useSelectSearch<Carrier, CarrierOptionType>({
    url: '/waste-management-system/carrier',
    filterName: 'name',
    optionsMap: item => ({
      ...item,
      label: item?.name,
      value: item?.cif
    }),
    initialSearchText: formik.values?.carrierName ?? ''
  })

  const delegateSelectSearch = useSelectSearch<Delegate, DelegateOptionType>({
    url: '/waste-management-system/delegate',
    filterName: 'name',
    optionsMap: item => ({
      ...item,
      label: item?.name,
      value: item?.cnp
    }),
    initialSearchText: formik.values?.delegateName ?? ''
  })

  const companySelectSearch = useSelectSearch<CollectingCompanyTableItem, CompanyOptionType>({
    url: '/waste-management-system/external-articles/waste-collection-companies',
    filterName: 'companyName',
    optionsMap: item => ({
      ...item,
      label: item?.name,
      value: item?.name
    }),
    initialSearchText: formik.values?.companyName ?? ''
  })

  return (
    <>
      <Grid item xs={12} className={s.title}>
        Deseuri
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="wasteCategories"
          label="Categorii deseuri"
          fullWidth
          variant="outlined"
          value={formik.values.wasteCategories}
          onChange={event => formik.setFieldValue('wasteCategories', event.currentTarget.value)}
          error={!!formik.errors.wasteCategories}
          helperText={formik.errors.wasteCategories as string}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="wasteCode"
          label="Cod deseuri"
          fullWidth
          variant="outlined"
          value={formik.values.wasteCode}
          onChange={event => formik.setFieldValue('wasteCode', event.currentTarget.value)}
          error={!!formik.errors.wasteCode}
          helperText={formik.errors.wasteCode as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <AsyncAutocomplete
          variant="outlined"
          name="operationTypes"
          label="Tip operatie (selectie multipla)"
          value={formik.values.operationTypes}
          options={operationTypesOptions || []}
          onChange={value => formik.setFieldValue('operationTypes', value)}
          error={!!formik.errors.operationTypes}
          helperText={formik.errors.operationTypes as string}
          required
          multiple
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="number"
          name="measuredWeight"
          label="Cantitate (kg)"
          fullWidth
          variant="outlined"
          value={formik.values.measuredWeight}
          {...getNonNegativeNumberInputHandlers(formik.setFieldValue, 'measuredWeight')}
          inputProps={{ min: 0 }}
          error={!!formik.errors.measuredWeight}
          helperText={formik.errors.measuredWeight as string}
          required
        />
      </Grid>
      <Grid item xs={12} className={s.title}>
        Expeditor
      </Grid>
      <Grid xs={12} md={12} item>
        <Input
          type="textarea"
          name="expeditorName"
          label="Date de identificare expeditor"
          fullWidth
          variant="outlined"
          value={`${currentLocationInfo.completeAddressStore}, CIF ${currentLocationInfo.fiscalCode}`}
          onChange={event => {}}
          required
          disabled
          width="100%"
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="expeditorEnvironmentAuthorizationNumber"
          label="Autorizatie de mediu nr."
          fullWidth
          variant="outlined"
          value={formik.values.expeditorEnvironmentAuthorizationNumber}
          onChange={event =>
            formik.setFieldValue(
              'expeditorEnvironmentAuthorizationNumber',
              event.currentTarget.value
            )
          }
          error={!!formik.errors.expeditorEnvironmentAuthorizationNumber}
          helperText={formik.errors.expeditorEnvironmentAuthorizationNumber as string}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="expeditorEnvironmentAuthorizationExpirationDate"
          label="Data la care expira autorizatia de mediu"
          fullWidth
          variant="outlined"
          value={formik.values.expeditorEnvironmentAuthorizationExpirationDate}
          onChange={event =>
            formik.setFieldValue(
              'expeditorEnvironmentAuthorizationExpirationDate',
              event.currentTarget.value
            )
          }
          error={!!formik.errors.expeditorEnvironmentAuthorizationExpirationDate}
          helperText={formik.errors.expeditorEnvironmentAuthorizationExpirationDate as string}
        />
      </Grid>
      <Grid item xs={12} className={s.title}>
        Transportator
      </Grid>
      <Grid xs={12} md={6} item>
        <Autocomplete
          {...carrierSelectSearch}
          label="Nume"
          variant="outlined"
          freeSolo
          autoComplete={false}
          filterOptions={x => x}
          value={formik.values.carrierName}
          onChange={(event, value) => {
            if (value?.name) {
              formik.setFieldValue('carrierId', value.id)
              formik.setFieldValue('carrierName', value.name)
              formik.setFieldValue('cif', value.cif)
              formik.setFieldValue('headquarter', value.headquarter)
            } else {
              formik.setFieldValue('carrierId', '')
              formik.setFieldValue('carrierName', value)
              formik.setFieldValue('cif', '')
              formik.setFieldValue('headquarter', '')
            }
          }}
          onClose={event => {
            if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
              formik.setFieldValue('carrierName', (event.target as HTMLInputElement).value)
              formik.setFieldValue('carrierId', '')
              formik.setFieldValue('cif', '')
              formik.setFieldValue('headquarter', '')
            }
          }}
          error={!!formik.errors.carrierName}
          helperText={formik.errors.carrierName as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="cif"
          label="CIF"
          fullWidth
          variant="outlined"
          value={formik.values.cif}
          onChange={event => formik.setFieldValue('cif', event.currentTarget.value)}
          error={!!formik.errors.cif}
          helperText={formik.errors.cif as string}
          required
        />
      </Grid>
      <Grid xs={12} md={12} item>
        <Input
          type="textarea"
          name="headquarter"
          label="Adresa"
          fullWidth
          variant="outlined"
          value={formik.values.headquarter}
          onChange={event => formik.setFieldValue('headquarter', event.currentTarget.value)}
          error={!!formik.errors.headquarter}
          helperText={formik.errors.headquarter as string}
          required
          width="100%"
          rows={2}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Autocomplete
          {...delegateSelectSearch}
          label="Nume delegat"
          variant="outlined"
          freeSolo
          autoComplete={false}
          filterOptions={x => x}
          value={formik.values.delegateName}
          onChange={(event, value) => {
            if (value?.name) {
              formik.setFieldValue('delegateId', value.id)
              formik.setFieldValue('delegateName', value.name)
              formik.setFieldValue('identityCardSeries', value.identityCardSeries)
              formik.setFieldValue('identityCardNumber', value.identityCardNumber)
              formik.setFieldValue('cnp', value.cnp)
              formik.setFieldValue('vehicleNumber', value.vehicleNumber)
              formik.setFieldValue('transportLicenseNumber', value.transportLicenseNumber)
              formik.setFieldValue(
                'transportLicenseExpirationDate',
                value.transportLicenseExpirationDate
              )
            } else {
              formik.setFieldValue('delegateId', '')
              formik.setFieldValue('delegateName', value)
              formik.setFieldValue('identityCardSeries', '')
              formik.setFieldValue('identityCardNumber', '')
              formik.setFieldValue('cnp', '')
              formik.setFieldValue('vehicleNumber', '')
              formik.setFieldValue('transportLicenseNumber', '')
              formik.setFieldValue('transportLicenseExpirationDate', '')
            }
          }}
          onClose={event => {
            if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
              formik.setFieldValue('delegateName', (event.target as HTMLInputElement).value)
              formik.setFieldValue('delegateId', '')
              formik.setFieldValue('identityCardSeries', '')
              formik.setFieldValue('identityCardNumber', '')
              formik.setFieldValue('cnp', '')
              formik.setFieldValue('vehicleNumber', '')
              formik.setFieldValue('transportLicenseNumber', '')
              formik.setFieldValue('transportLicenseExpirationDate', '')
            }
          }}
          error={!!formik.errors.delegateName}
          helperText={formik.errors.delegateName as string}
          required
        />
      </Grid>
      <Grid xs={6} md={3} item>
        <Input
          type="text"
          name="identityCardSeries"
          label="Serie CI"
          fullWidth
          variant="outlined"
          value={formik.values.identityCardSeries}
          onChange={event => formik.setFieldValue('identityCardSeries', event.currentTarget.value)}
          error={!!formik.errors.identityCardSeries}
          helperText={formik.errors.identityCardSeries as string}
        />
      </Grid>
      <Grid xs={6} md={3} item>
        <Input
          type="text"
          name="identityCardNumber"
          label="Nr CI"
          fullWidth
          variant="outlined"
          value={formik.values.identityCardNumber}
          onChange={event => formik.setFieldValue('identityCardNumber', event.currentTarget.value)}
          error={!!formik.errors.identityCardNumber}
          helperText={formik.errors.identityCardNumber as string}
        />
      </Grid>
      <Grid xs={6} md={6} item>
        <Input
          type="text"
          name="cnp"
          label="CNP"
          fullWidth
          variant="outlined"
          value={formik.values.cnp}
          onChange={event => formik.setFieldValue('cnp', event.currentTarget.value)}
          error={!!formik.errors.cnp}
          helperText={formik.errors.cnp as string}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="vehicleNumber"
          label="Nr. de inmatriculare mijloc transport"
          fullWidth
          variant="outlined"
          value={formik.values.vehicleNumber}
          onChange={event => formik.setFieldValue('vehicleNumber', event.currentTarget.value)}
          error={!!formik.errors.vehicleNumber}
          helperText={formik.errors.vehicleNumber as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="transportLicenseNumber"
          label="Licenta de transport marfuri nepericuloase nr."
          fullWidth
          variant="outlined"
          value={formik.values.transportLicenseNumber}
          onChange={event =>
            formik.setFieldValue('transportLicenseNumber', event.currentTarget.value)
          }
          error={!!formik.errors.transportLicenseNumber}
          helperText={formik.errors.transportLicenseNumber as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="transportLicenseExpirationDate"
          label="Data la care expira licenta de transport marfuri"
          fullWidth
          variant="outlined"
          value={formik.values.transportLicenseExpirationDate}
          onChange={event =>
            formik.setFieldValue('transportLicenseExpirationDate', event.currentTarget.value)
          }
          error={!!formik.errors.transportLicenseExpirationDate}
          helperText={formik.errors.transportLicenseExpirationDate as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <DatePicker
          label="Data incarcare"
          onChange={value => {}}
          variant="outlined"
          required
          value={dayjs(formik.values.carrierHandoverDate)}
          style={{ width: '100%' }}
          disabled
        />
      </Grid>
      <Grid item xs={12} className={s.title}>
        Destinatar
      </Grid>
      {isExternalArticlesReport ? (
        <>
          <Grid xs={12} md={12} item>
            <Autocomplete
              {...companySelectSearch}
              label="Nume destinatar"
              variant="outlined"
              freeSolo
              autoComplete={false}
              filterOptions={x => x}
              value={formik.values.companyName}
              onChange={(event, value) => {
                if (value?.name) {
                  formik.setFieldValue('companyId', value.id)
                  formik.setFieldValue('companyName', value.name)
                  formik.setFieldValue('companyHeadquarter', value.headquarter)
                  formik.setFieldValue('companyCif', value.cif)
                  formik.setFieldValue('companyTradeRegisterNumber', value.tradeRegisterNumber)
                  formik.setFieldValue('companyCounty', value.county)
                  formik.setFieldValue(
                    'environmentalAuthorizationNo',
                    value.environmentalPermitNumber
                  )
                  formik.setFieldValue(
                    'environmentalPermitExpirationDate',
                    value.environmentalPermitExpirationDate
                  )
                } else {
                  formik.setFieldValue('companyId', '')
                  formik.setFieldValue('companyName', value)
                  formik.setFieldValue('companyHeadquarter', '')
                  formik.setFieldValue('companyCif', '')
                  formik.setFieldValue('companyTradeRegisterNumber', '')
                  formik.setFieldValue('companyCounty', '')
                  formik.setFieldValue('environmentalAuthorizationNo', '')
                  formik.setFieldValue('environmentalPermitExpirationDate', '')
                }
              }}
              onClose={event => {
                if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
                  formik.setFieldValue('companyName', (event.target as HTMLInputElement).value)
                  formik.setFieldValue('companyId', '')
                  formik.setFieldValue('companyHeadquarter', '')
                  formik.setFieldValue('companyCif', '')
                  formik.setFieldValue('companyTradeRegisterNumber', '')
                  formik.setFieldValue('companyCounty', '')
                  formik.setFieldValue('environmentalAuthorizationNo', '')
                  formik.setFieldValue('environmentalPermitExpirationDate', '')
                }
              }}
              error={!!formik.errors.companyName}
              helperText={formik.errors.companyName as string}
              required
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <Input
              type="text"
              name="companyTradeRegisterNumber"
              label="Numar registrul comertului"
              fullWidth
              variant="outlined"
              value={formik.values.companyTradeRegisterNumber}
              onChange={event =>
                formik.setFieldValue('companyTradeRegisterNumber', event.currentTarget.value)
              }
              error={!!formik.errors.companyTradeRegisterNumber}
              helperText={formik.errors.companyTradeRegisterNumber as string}
              disabled={!!formik.values.companyId}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <Input
              type="text"
              name="companyCif"
              label="CIF"
              fullWidth
              variant="outlined"
              value={formik.values.companyCif}
              onChange={event => formik.setFieldValue('companyCif', event.currentTarget.value)}
              error={!!formik.errors.companyCif}
              helperText={formik.errors.companyCif as string}
              required
              disabled={!!formik.values.companyId}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <Input
              type="text"
              name="companyHeadquarter"
              label="Sediul central"
              fullWidth
              variant="outlined"
              value={formik.values.companyHeadquarter}
              onChange={event =>
                formik.setFieldValue('companyHeadquarter', event.currentTarget.value)
              }
              error={!!formik.errors.companyHeadquarter}
              helperText={formik.errors.companyHeadquarter as string}
              required
              disabled={!!formik.values.companyId}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <Input
              type="text"
              name="companyCounty"
              label="Judet"
              fullWidth
              variant="outlined"
              value={formik.values.companyCounty}
              onChange={event => formik.setFieldValue('companyCounty', event.currentTarget.value)}
              error={!!formik.errors.companyCounty}
              helperText={formik.errors.companyCounty as string}
              required
              disabled={!!formik.values.companyId}
            />
          </Grid>
        </>
      ) : (
        <Grid xs={12} md={12} item>
          <Input
            type="textarea"
            name="receiverName"
            label="Date de identificare destinatar"
            variant="outlined"
            value={currentCompany?.description}
            onChange={event => {}}
            required
            width="100%"
            disabled
          />
        </Grid>
      )}
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="environmentalAuthorizationNo"
          label="Autorizatie de mediu nr."
          fullWidth
          variant="outlined"
          value={formik.values.environmentalAuthorizationNo}
          onChange={event =>
            formik.setFieldValue('environmentalAuthorizationNo', event.currentTarget.value)
          }
          error={!!formik.errors.environmentalAuthorizationNo}
          helperText={formik.errors.environmentalAuthorizationNo as string}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="environmentalPermitExpirationDate"
          label="Data la care expira autorizatia de mediu"
          fullWidth
          variant="outlined"
          value={formik.values.environmentalPermitExpirationDate}
          onChange={event =>
            formik.setFieldValue('environmentalPermitExpirationDate', event.currentTarget.value)
          }
          error={!!formik.errors.environmentalPermitExpirationDate}
          helperText={formik.errors.environmentalPermitExpirationDate as string}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <DatePicker
          label="Data descarcare"
          onChange={value => formik.setFieldValue('downloadDate', value)}
          variant="outlined"
          helperText={formik.errors.downloadDate as string}
          error={!!formik.errors.downloadDate}
          value={formik.values.downloadDate ? dayjs(formik.values.downloadDate) : ''}
          style={{ width: '100%' }}
        />
      </Grid>
    </>
  )
}

export default NonDangerousDEEEReportForm
